import { RoutesKey } from '@/models/enums/app/RoutesKey'
import { i18n } from '@/plugins/i18n'
import { createRouter, createWebHistory } from 'vue-router'
import { pinia } from '@/plugins/index'
import { useApplicationStore } from '@/stores/applicationStore'
import { useUserStore } from '@/stores/userStore'
import { useAdminStore } from '@/stores/adminStore.ts'
import { AdministrationPanels } from '@/models/enums/app/AdministrationPanels'
import { UserRoles } from '@/models/enums/auth/UserRoles'

// Imports des vues
import AuthSignIn from '@/views/auth/AuthSignIn.vue'
import AuthSignup from '@/views/auth/AuthSignUp.vue'
import HomeView from '@/views/home/HomeView.vue'
import MyMapView from '@/views/map/MyMapView.vue'
import AdminMembers from '@/views/admin/components/AdminMembers.vue'
import AdminContent from '@/views/admin/components/AdminContent.vue'
import AuthEmailVerifierSend from '@/views/auth/AuthEmailVerifierSend.vue'
import AuthEmailVerifier from '@/views/auth/AuthEmailVerifier.vue'
import AuthForgotPassword from '@/views/auth/AuthForgotPassword.vue'
import AuthResetPasswordOk from '@/views/auth/AuthResetPasswordOk.vue'
import AuthResetPassword from '@/views/auth/AuthResetPassword.vue'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    return savedPosition ? savedPosition : { el: '#app', top: 0 }
  },
  routes: [
    {
      path: '/',
      name: RoutesKey.HOME,
      component: HomeView,
      // L'accueil national est réservé aux comptes centraux et aux visiteurs : un profil communal
      // est renvoyé vers l'espace de sa commune (l'authentification est vérifiée par beforeEach).
      beforeEnter: (to, from, next) => {
        const espace = useUserStore(pinia).espaceCommunal()
        if (espace) {
          next(espace)
        } else {
          next()
        }
      },
    },
    {
      path: `/${i18n.t('routes.signIn')}`,
      name: RoutesKey.AUTH_SIGN_IN,
      component: AuthSignIn,
      meta: { requiresUnauth: true },
    },
    {
      path: `/${i18n.t('routes.signUp')}`,
      name: RoutesKey.AUTH_SIGN_UP,
      component: AuthSignup,
      meta: { requiresUnauth: true },
    },
    {
      path: '/password_forgot',
      name: RoutesKey.AUTH_FORGOT_PASSWORD,
      component: AuthForgotPassword,
      meta: { requiresUnauth: true },
    },
    {
      path: '/reset_password_ok',
      name: RoutesKey.AUTH_RESET_PASSWORD_OK,
      component: AuthResetPasswordOk,
      meta: { requiresUnauth: true },
    },
    {
      path: '/email-verifier-ask',
      name: RoutesKey.AUTH_EMAIL_VERIFIER_ASK,
      component: AuthEmailVerifierSend,
      meta: { requiresUnauth: true },
    },
    {
      path: '/email-verifier',
      name: RoutesKey.AUTH_EMAIL_VERIFIER,
      component: AuthEmailVerifier,
      meta: { requiresUnauth: true },
    },
    {
      path: '/reset-password',
      name: RoutesKey.AUTH_RESET_PASSWORD,
      component: AuthResetPassword,
      meta: { requiresUnauth: true },
    },
    {
      path: '/geoportail',
      name: RoutesKey.GEOPORTAIL,
      component: MyMapView,
    },
    {
      path: '/projets/:id',
      redirect: (to) => ({ path: `/projects/${String(to.params.id)}` }),
    },
    {
      path: '/projects/:id',
      name: RoutesKey.PROJECT_PAGE,
      component: () => import('@/views/projects/ProjectDetailView.vue'),
    },
    {
      path: '/infrastructure',
      name: RoutesKey.INFRASTRUCTURE_FICHE,
      component: () => import('@/views/infrastructure/InfrastructureFicheView.vue'),
    },
    {
      // Tableau de bord exécutif du maire (§3.1 à §3.3). Réservé à l'habilitation
      // ACCES_DASHBOARD_COMMUNAL (l'Administrateur Commune par défaut) : le backend renvoie 403 sur
      // une commune hors du territoire du profil, la garde évite d'ouvrir une vue vide.
      path: '/tableau-de-bord',
      name: RoutesKey.DASHBOARD_MAIRE,
      component: () => import('@/views/dashboard/DashboardMaireView.vue'),
      beforeEnter: async (to, from, next) => {
        const applicationStore = useApplicationStore(pinia)
        const userStore = useUserStore(pinia)

        applicationStore.isLoading = true

        if (!userStore.loginCheck) {
          await userStore.checkAuthenticated()
        }

        const peutConsulter = !!userStore.userHasRole(UserRoles.ACCES_DASHBOARD_COMMUNAL)

        applicationStore.isLoading = false

        if (!peutConsulter) {
          next({ path: '/' })
        } else {
          next()
        }
      },
    },
    {
      // Tableau de bord exécutif — vue nationale (§ chantier tableau de bord central). Réservé à
      // l'habilitation ACCES_DASHBOARD_CENTRAL (Super Administrateur et Administrateur Fonctionnel
      // par défaut).
      path: '/tableau-de-bord-central',
      name: RoutesKey.DASHBOARD_CENTRAL,
      component: () => import('@/views/dashboard/DashboardCentralView.vue'),
      beforeEnter: async (to, from, next) => {
        const applicationStore = useApplicationStore(pinia)
        const userStore = useUserStore(pinia)

        applicationStore.isLoading = true

        if (!userStore.loginCheck) {
          await userStore.checkAuthenticated()
        }

        const peutConsulter = userStore.userPeutConsulterDashboardCentral

        applicationStore.isLoading = false

        if (!peutConsulter) {
          next({ path: '/' })
        } else {
          next()
        }
      },
    },
    {
      path: '/myAccount',
      name: RoutesKey.USERACCOUNT,
      component: () => import('@/views/member/MemberView.vue'),
      beforeEnter: async (to, from, next) => {
        const applicationStore = useApplicationStore(pinia)
        const userStore = useUserStore(pinia)

        applicationStore.isLoading = true

        if (!userStore.loginCheck) {
          await userStore.checkAuthenticated()
        }

        if (!userStore.userIsLogged) {
          applicationStore.isLoading = false
          next({ path: '/' })
        } else {
          next()
        }
      },
    },
    {
      path: '/natural-language-query',
      name: RoutesKey.NATURAL_LANGUAGE_QUERY,
      component: () => import('@/views/naturalLanguageQuery/NaturalLanguageQueryView.vue'),
      beforeEnter: async (to, from, next) => {
        const applicationStore = useApplicationStore(pinia)
        const userStore = useUserStore(pinia)

        applicationStore.isLoading = true

        if (!userStore.loginCheck) {
          await userStore.checkAuthenticated()
        }

        if (!userStore.userIsLogged) {
          applicationStore.isLoading = false
          next({ path: '/' })
        } else {
          next()
        }
      },
    },
    {
      path: '/admin',
      name: RoutesKey.ADMIN,
      redirect: () => {
        const adminStore = useAdminStore(pinia)
        adminStore.selectedAdminPanel = AdministrationPanels.MEMBERS
        return { name: 'adminUsers' }
      },
      component: () => import('@/views/admin/AdminView.vue'),
      beforeEnter: async (to, from, next) => {
        const applicationStore = useApplicationStore(pinia)
        const userStore = useUserStore(pinia)

        applicationStore.isLoading = true

        if (!userStore.loginCheck) {
          await userStore.checkAuthenticated()
        }

        if (!userStore.userAccedeAdministration()) {
          applicationStore.isLoading = false
          next({ path: '/' })
        } else {
          next()
        }
      },
      children: [
        {
          path: 'users',
          name: RoutesKey.ADMINUSER,
          component: AdminMembers,
          // Écran d'arrivée de /admin : un profil à qui l'administration a été ouverte sans la
          // gestion des comptes arrive sur les contenus.
          beforeEnter: (_to, _from, next) => {
            const userStore = useUserStore(pinia)
            if (userStore.userPeutListerUtilisateurs()) next()
            else if (userStore.userIsAdmin()) next({ name: RoutesKey.ADMINPROJECTS })
            else next({ name: RoutesKey.ADMIN_SYNCHRONISATION })
          },
        },
        {
          // Habilitations accordées par chaque profil (securite.habprofil).
          path: 'habilitations',
          name: RoutesKey.ADMIN_HABILITATIONS,
          component: () => import('@/views/admin/components/admin-members/AdminHabilitations.vue'),
          beforeEnter: (_to, _from, next) => {
            if (useUserStore(pinia).userPeutGererHabilitations()) next()
            else next({ name: RoutesKey.ADMINUSER })
          },
        },
        {
          // Synchronisation avec la plateforme en ligne (instance locale) ou suivi des instances
          // locales (plateforme en ligne).
          path: 'synchronisation',
          name: RoutesKey.ADMIN_SYNCHRONISATION,
          component: () =>
            import('@/views/admin/components/admin-members/AdminSynchronisation.vue'),
          beforeEnter: (_to, _from, next) => {
            if (useUserStore(pinia).userPeutGererSynchronisation()) next()
            else next({ name: RoutesKey.ADMINUSER })
          },
        },
        {
          path: 'content',
          name: RoutesKey.ADMINCONTENT,
          component: AdminContent,
          redirect: () => ({ name: 'adminActors' }),
          children: [
            {
              path: 'projects',
              name: RoutesKey.ADMINPROJECTS,
              component: () =>
                import('@/views/admin/components/admin-content/AdminProjectsPanel.vue'),
            },
            {
              path: 'resources',
              name: RoutesKey.ADMINRESOURCES,
              component: () =>
                import('@/views/admin/components/admin-content/AdminResourcesPanel.vue'),
            },
          ],
        },
        {
          path: 'parametrage',
          name: RoutesKey.ADMIN_PARAMETRAGE,
          component: () =>
            import('@/views/admin/components/parametrage/AdminParametrageLayout.vue'),
          redirect: () => {
            const userStore = useUserStore(pinia)
            if (userStore.userPeutGererIndicateurs())
              return { name: RoutesKey.ADMIN_INDICATOR_LIST }
            if (userStore.userPeutGererThematiques())
              return { name: RoutesKey.ADMIN_THEMATIQUES_DOMAINES }
            return { name: RoutesKey.ADMIN_INFRASTRUCTURE_TYPES }
          },
          // Chaque écran exige son habilitation (meta.droit) : GESTION_INDICATEURS,
          // GESTION_THEMATIQUES ou, par défaut, GESTION_PARAMETRAGE.
          beforeEnter: async (to, _from, next) => {
            const userStore = useUserStore(pinia)
            if (!userStore.userAccedeAuParametrage()) {
              next({ name: RoutesKey.ADMINUSER })
            } else {
              next()
            }
          },
          children: [
            {
              path: 'indicators',
              name: RoutesKey.ADMIN_INDICATOR_LIST,
              meta: { droit: 'indicateurs' },
              component: () =>
                import('@/views/admin/components/parametrage/AdminIndicatorListPanel.vue'),
            },
            {
              path: 'indicators/:id/edit',
              name: RoutesKey.ADMIN_INDICATOR_EDIT,
              meta: { droit: 'indicateurs' },
              component: () =>
                import('@/views/admin/components/parametrage/AdminIndicatorEditPanel.vue'),
            },
            {
              // Indicateur communal rattaché à un sous-domaine : même écran que la création.
              path: 'indicators/:id/communal',
              name: RoutesKey.ADMIN_INDICATOR_COMMUNAL_EDIT,
              meta: { droit: 'indicateurs' },
              component: () =>
                import('@/views/admin/components/parametrage/AdminIndicatorCreationPanel.vue'),
            },
            {
              path: 'creation-indicateurs',
              name: RoutesKey.ADMIN_INDICATOR_CREATION,
              meta: { droit: 'indicateurs' },
              component: () =>
                import('@/views/admin/components/parametrage/AdminIndicatorCreationPanel.vue'),
            },
            {
              // Indicateurs cartographiques (ratio, densité, zone tampon, complétude ; UAD).
              path: 'creation-indicateurs/avancee',
              name: RoutesKey.ADMIN_INDICATOR_ADVANCED_CREATION,
              meta: { droit: 'indicateurs' },
              component: () =>
                import(
                  '@/views/admin/components/parametrage/AdminIndicatorAdvancedCreationPanel.vue'
                ),
            },
            {
              path: 'thematiques/domaines',
              name: RoutesKey.ADMIN_THEMATIQUES_DOMAINES,
              meta: { droit: 'thematiques', niveauThematique: 'domaines' },
              component: () =>
                import('@/views/admin/components/parametrage/AdminReferentielThematiquePanel.vue'),
            },
            {
              path: 'thematiques/sous-domaines',
              name: RoutesKey.ADMIN_THEMATIQUES_SOUSDOMAINES,
              meta: { droit: 'thematiques', niveauThematique: 'sousdomaines' },
              component: () =>
                import('@/views/admin/components/parametrage/AdminReferentielThematiquePanel.vue'),
            },
            {
              path: 'thematiques/competences',
              name: RoutesKey.ADMIN_THEMATIQUES_COMPETENCES,
              meta: { droit: 'thematiques', niveauThematique: 'competences' },
              component: () =>
                import('@/views/admin/components/parametrage/AdminReferentielThematiquePanel.vue'),
            },
            {
              path: 'infrastructure-types',
              name: RoutesKey.ADMIN_INFRASTRUCTURE_TYPES,
              component: () =>
                import('@/views/admin/components/parametrage/AdminInfrastructureTypesPanel.vue'),
            },
            {
              path: 'infrastructure-attributes',
              name: RoutesKey.ADMIN_INFRASTRUCTURE_ATTRIBUTES,
              component: () =>
                import(
                  '@/views/admin/components/parametrage/AdminInfrastructureAttributesPanel.vue'
                ),
            },
            {
              path: 'infrastructure-fiche-data',
              name: RoutesKey.ADMIN_INFRASTRUCTURE_FICHE_DATA,
              component: () =>
                import(
                  '@/views/admin/components/parametrage/AdminInfrastructureFicheDataPanel.vue'
                ),
            },
            {
              path: 'attribute-choices',
              name: RoutesKey.ADMIN_ATTRIBUT_CHOIX,
              component: () =>
                import('@/views/admin/components/parametrage/AdminAttributeChoicesPanel.vue'),
            },
            {
              path: 'atouts',
              name: RoutesKey.ADMIN_ATOUTS_LIST,
              component: () =>
                import('@/views/admin/components/parametrage/AdminAtoutsListPanel.vue'),
            },
            {
              path: 'map-styles',
              name: RoutesKey.ADMIN_MAP_STYLES_LIST,
              component: () =>
                import('@/views/admin/components/parametrage/AdminMapStylesListPanel.vue'),
            },
            {
              path: 'map-styles/:id',
              name: RoutesKey.ADMIN_MAP_STYLE_EDIT,
              component: () =>
                import('@/views/admin/components/parametrage/AdminMapStyleEditPanel.vue'),
            },
            {
              path: 'osm-map-styles',
              name: RoutesKey.ADMIN_OSM_MAP_STYLES_LIST,
              component: () =>
                import('@/views/admin/components/parametrage/AdminOsmMapStylesListPanel.vue'),
            },
            {
              path: 'icons',
              name: RoutesKey.ADMIN_ICONS,
              component: () => import('@/views/admin/components/parametrage/AdminIconsPanel.vue'),
            },
          ],
        },
      ],
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: { name: RoutesKey.HOME },
    },
  ],
})

router.beforeEach(async (to, from, next) => {
  const userStore = useUserStore(pinia)

  if (!userStore.loginCheck) {
    try {
      await userStore.checkAuthenticated()
    } catch (e) {
      console.error('Auth check failed during navigation: ', e)
    }
  }

  if (
    to.query.dialog === 'verify-email' &&
    to.query.token &&
    to.name !== RoutesKey.AUTH_EMAIL_VERIFIER
  ) {
    return next({
      name: RoutesKey.AUTH_EMAIL_VERIFIER,
      query: to.query,
    })
  }

  if (
    to.query.dialog === 'reset-password' &&
    to.query.token &&
    to.name !== RoutesKey.AUTH_RESET_PASSWORD
  ) {
    return next({
      name: RoutesKey.AUTH_RESET_PASSWORD,
      query: to.query,
    })
  }

  if (to.meta.requiresUnauth && userStore.userIsLogged) {
    next({ path: '/' })
    return
  }

  // Écrans de « Paramétrage & Indicateur » : chacun son habilitation (meta.droit), vérifiée aussi
  // en passant d'un écran à l'autre (le beforeEnter du parent ne voit que l'entrée dans le menu).
  if (
    to.matched.some((r) => r.name === RoutesKey.ADMIN_PARAMETRAGE) &&
    to.name !== RoutesKey.ADMIN_PARAMETRAGE
  ) {
    const droit = (to.meta.droit as string | undefined) ?? 'parametrage'
    const accorde =
      droit === 'indicateurs'
        ? userStore.userPeutGererIndicateurs()
        : droit === 'thematiques'
          ? userStore.userPeutGererThematiques()
          : userStore.userPeutParametrer()
    if (!accorde) {
      next(
        userStore.userAccedeAuParametrage()
          ? { name: RoutesKey.ADMIN_PARAMETRAGE }
          : { name: RoutesKey.ADMINUSER },
      )
      return
    }
  }

  next()
})

router.onError((err, to) => {
  if (err?.message?.includes?.('Failed to fetch dynamically imported module')) {
    if (!localStorage.getItem('app:dynamic-reload')) {
      localStorage.setItem('app:dynamic-reload', 'true')
      location.assign(to.fullPath)
    } else {
      console.error('Dynamic import error persists', err)
    }
  } else {
    console.error(err)
  }
})

const initRouter = async () => {
  try {
    await router.isReady()
    localStorage.removeItem('app:dynamic-reload')
  } catch (err) {
    console.error('Router isReady failed:', err)
  }
}

initRouter()

export default router
